import { useState, useEffect, useCallback } from "react";
import { FormControl, FormLabel } from "@chakra-ui/react";
import { LANGUAGES } from "../../constants/languages";
import AutoComplete from "../common/AutoComplete";

type Props = {
  label: string;
  value: string;
  onChange: (lang: string) => void;
};

export const LanguageSelector: React.FC<Props> = ({
  label,
  value,
  onChange,
}) => {
  // Map language names ↔︎ codes
  const entries = Object.entries(LANGUAGES) as [string, string][];
  const allNames = entries.map(([name]) => name);

  // name shown in input
  const [selectedName, setSelectedName] = useState<string>("");

  // current suggestions
  const [suggestions, setSuggestions] = useState<string[]>(allNames);

  // keep selectedName in sync when value (code) changes from outside
  useEffect(() => {
    const found = entries.find(([, code]) => code === value);
    setSelectedName(found ? found[0] : "");
  }, [value, entries]);

  // filter suggestions on user input
  const handleSearch = useCallback(
    (q: string) => {
      const lower = q.trim().toLowerCase();
      if (!lower) {
        setSuggestions(allNames);
      } else {
        setSuggestions(
          allNames.filter((name) => {
            const code = LANGUAGES[name];
            return (
              name.toLowerCase().includes(lower) ||
              code.toLowerCase().includes(lower)
            );
          }),
        );
      }
    },
    [allNames],
  );

  // when a user picks from the list
  const handleSelect = (name: string) => {
    setSelectedName(name);
    onChange(LANGUAGES[name]);
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <AutoComplete
        value={selectedName}
        suggestions={suggestions}
        onSearch={handleSearch}
        onSelect={handleSelect}
        placeholder="Search language..."
      />
    </FormControl>
  );
};
